import { Breadcrumb } from '@/components/common/Breadcrumb';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { 
  Search, 
  Calendar, 
  Clock, 
  ArrowRight, 
  Grid, 
  List, 
  X, 
  Radio,
  Play,
  Award,
  Layers,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Filter,
  RotateCcw,
  ArrowUpDown,
  CalendarDays,
  Users,
  Building2,
  Timer,
  Hourglass
} from 'lucide-react';
import { seminarsAPI, industriesAPI, speakersAPI } from '@/services/api';
import { format, isWithinInterval, isSameDay, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { formatET, getETAbbr } from '@/utils/dateUtils';
import { WebinarCard } from '@/components/common/WebinarCard';

const ITEMS_PER_PAGE = 20;

// HR Compliance industry check for CPE badge display
const HR_COMPLIANCE_NAMES = ['HR Compliance', 'HR compliance', 'Human Resources Compliance'];

// Helper to check if seminar is HR Compliance industry (for CPE badge display)
const isHRCompliance = (industryName, subIndustryName) => {
  if (!industryName && !subIndustryName) return false;
  const nameToCheck = subIndustryName || industryName || '';
  return HR_COMPLIANCE_NAMES.some(hr => 
    nameToCheck.toLowerCase().includes(hr.toLowerCase())
  );
};

// HRCI Logo URL for CPE badge
const HRCI_LOGO_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api/static/images/hrci-logo.svg`;

// Industry-based fallback images (Corporate Training Style - Generated per industry)
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const industryImages = {
  'Cross Industry Functions': `${BASE_URL}/api/static/images/industries/cross_industry_functions_1dc8cb34.png`,
  'Healthcare and Hospital': `${BASE_URL}/api/static/images/industries/healthcare_and_hospital_40b680e4.png`,
  'Healthcare': `${BASE_URL}/api/static/images/industries/healthcare_and_hospital_40b680e4.png`,
  'Banking Financial Services and Insurance': `${BASE_URL}/api/static/images/industries/banking_financial_services_and_insurance_5dc7a75a.png`,
  'Manufacturing': `${BASE_URL}/api/static/images/industries/manufacturing_4fbaa269.png`,
  'Construction': `${BASE_URL}/api/static/images/industries/construction_55779e1e.png`,
  'Legal and Law': `${BASE_URL}/api/static/images/industries/legal_and_law_e7a3978e.png`,
  'Legal': `${BASE_URL}/api/static/images/industries/legal_and_law_e7a3978e.png`,
  'Life Sciences': `${BASE_URL}/api/static/images/industries/life_sciences_e1dad885.png`,
  'Banks and Credit Unions': `${BASE_URL}/api/static/images/industries/banks_and_credit_unions_325709aa.png`,
  'Banking': `${BASE_URL}/api/static/images/industries/banks_and_credit_unions_325709aa.png`,
  'Financial Services and Trading': `${BASE_URL}/api/static/images/industries/financial_services_and_trading_89ceb2d8.png`,
  'Financial': `${BASE_URL}/api/static/images/industries/financial_services_and_trading_89ceb2d8.png`,
  'Insurance': `${BASE_URL}/api/static/images/industries/insurance_e60007e4.png`,
  'Medical Devices': `${BASE_URL}/api/static/images/industries/medical_devices_179555d1.png`,
  'Drugs & Chemicals': `${BASE_URL}/api/static/images/industries/drugs_chemicals_1e4925e4.png`,
  'Biotechnology': `${BASE_URL}/api/static/images/industries/biotechnology_c1285ae0.png`,
  'Clinical Trial and Clinical Research': `${BASE_URL}/api/static/images/industries/clinical_trial_and_clinical_research_f072cf4a.png`,
  'Clinical': `${BASE_URL}/api/static/images/industries/clinical_trial_and_clinical_research_f072cf4a.png`,
  'Laboratory': `${BASE_URL}/api/static/images/industries/laboratory_1900608a.png`,
  'Food and Dietary Supplements': `${BASE_URL}/api/static/images/industries/food_and_dietary_supplements_2a4f16b1.png`,
  'All FDA Regulated Industry': `${BASE_URL}/api/static/images/industries/all_fda_regulated_industry_4b39b1e0.png`,
  'FDA': `${BASE_URL}/api/static/images/industries/all_fda_regulated_industry_4b39b1e0.png`,
  'Pharmaceutical': `${BASE_URL}/api/static/images/industries/pharmaceutical_055fbb4f.png`,
  'Quality and Safety': `${BASE_URL}/api/static/images/industries/quality_and_safety_74265bc8.png`,
  'Quality': `${BASE_URL}/api/static/images/industries/quality_and_safety_74265bc8.png`,
  'Standards and Methodology': `${BASE_URL}/api/static/images/industries/standards_and_methodology_6a88d70c.png`,
  'Quality Management': `${BASE_URL}/api/static/images/industries/quality_management_351b80cf.png`,
  'Medicaid and Medicare': `${BASE_URL}/api/static/images/industries/medicaid_and_medicare_a96df4ca.png`,
  'HIPAA / HITECH': `${BASE_URL}/api/static/images/industries/hipaa_hitech_5db49ef2.png`,
  'HIPAA': `${BASE_URL}/api/static/images/industries/hipaa_hitech_5db49ef2.png`,
  'Audits & Data Security': `${BASE_URL}/api/static/images/industries/audits_data_security_50fb4653.png`,
  'Hospital Management': `${BASE_URL}/api/static/images/industries/hospital_management_3ab9dc5e.png`,
  'Corporate Governance': `${BASE_URL}/api/static/images/industries/corporate_governance_ab00e884.png`,
  'IT and PCI Compliance': `${BASE_URL}/api/static/images/industries/it_and_pci_compliance_78427d1a.png`,
  'IT': `${BASE_URL}/api/static/images/industries/it_and_pci_compliance_78427d1a.png`,
  'HR Compliance': `${BASE_URL}/api/static/images/industries/hr_compliance_b55d1eb0.png`,
  'HR': `${BASE_URL}/api/static/images/industries/hr_compliance_b55d1eb0.png`,
  'OSHA Compliance': `${BASE_URL}/api/static/images/industries/osha_compliance_59ebc134.png`,
  'OSHA': `${BASE_URL}/api/static/images/industries/osha_compliance_59ebc134.png`,
  'EHS Compliance': `${BASE_URL}/api/static/images/industries/ehs_compliance_5156d634.png`,
  'Trade and Logistics': `${BASE_URL}/api/static/images/industries/trade_and_logistics_747e3358.png`,
  'Accounting and Taxation': `${BASE_URL}/api/static/images/industries/accounting_and_taxation_303fa4d6.png`,
  'Accounting': `${BASE_URL}/api/static/images/industries/accounting_and_taxation_303fa4d6.png`,
  'Payroll Benefits and Compensation': `${BASE_URL}/api/static/images/industries/payroll_benefits_and_compensation_a99ed275.png`,
  'Payroll': `${BASE_URL}/api/static/images/industries/payroll_benefits_and_compensation_a99ed275.png`,
  'Excel Training': `${BASE_URL}/api/static/images/industries/excel_training_36a022f1.png`,
  'Excel': `${BASE_URL}/api/static/images/industries/excel_training_36a022f1.png`,
  'Outlook Training': `${BASE_URL}/api/static/images/industries/outlook_training_c2a7a416.png`,
  'Outlook': `${BASE_URL}/api/static/images/industries/outlook_training_c2a7a416.png`,
  'Sales And Customer Service Coaching': `${BASE_URL}/api/static/images/industries/sales_and_customer_service_coaching_bdd251e3.png`,
  'Sales': `${BASE_URL}/api/static/images/industries/sales_and_customer_service_coaching_bdd251e3.png`,
  'Project Management': `${BASE_URL}/api/static/images/industries/project_management_29eb7e60.png`,
  'Risk Management': `${BASE_URL}/api/static/images/industries/risk_management_68769a2e.png`,
  'Communication and Business Writing': `${BASE_URL}/api/static/images/industries/communication_and_business_writing_0917d5ed.png`,
  'Communication': `${BASE_URL}/api/static/images/industries/communication_and_business_writing_0917d5ed.png`,
  'Public Housing & Compliance': `${BASE_URL}/api/static/images/industries/public_housing_compliance_3c8da890.png`,
  'Law Reform': `${BASE_URL}/api/static/images/industries/law_reform_ce6ff262.png`,
  'Law Enforcement': `${BASE_URL}/api/static/images/industries/law_enforcement_514a4fb6.png`,
  'Generic Webinars': `${BASE_URL}/api/static/images/industries/generic_webinars_4eb618cf.png`,
  'default': `${BASE_URL}/api/static/images/industries/generic_webinars_4eb618cf.png`
};

// Get appropriate thumbnail for seminar based on industry
const getSeminarThumbnail = (thumbnailUrl, industryName) => {
  // Check if thumbnail URL is valid (not empty, not pointing to old preview domain)
  if (thumbnailUrl && !thumbnailUrl.includes('preview.emergentagent.com') && !thumbnailUrl.includes('undefined')) {
    return thumbnailUrl;
  }
  
  // Find exact match first
  if (industryName && industryImages[industryName]) {
    return industryImages[industryName];
  }
  
  // Find partial match
  if (industryName) {
    for (const [key, url] of Object.entries(industryImages)) {
      if (industryName.toLowerCase().includes(key.toLowerCase()) || 
          key.toLowerCase().includes(industryName.toLowerCase())) {
        return url;
      }
    }
  }
  
  return industryImages.default;
};

// Seminar type filter options
const SEMINAR_TYPES = [
  { id: 'live', label: 'Live Seminars', icon: Radio, color: 'text-red-500 bg-red-50' },
  { id: 'recorded', label: 'Recorded Seminars', icon: Play, color: 'text-blue-500 bg-blue-50' },
  { id: 'certified', label: 'CPE Certified', icon: Award, color: 'text-green-500 bg-green-50' },
  { id: 'series', label: 'Seminar Series', icon: Layers, color: 'text-purple-500 bg-purple-50' },
  { id: 'bestseller', label: 'Best Sellers', icon: TrendingUp, color: 'text-gold-500 bg-gold-50' },
];

// Sort options
const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest First' },
  { id: 'oldest', label: 'Oldest First' },
  { id: 'price_low', label: 'Price: Low to High' },
  { id: 'price_high', label: 'Price: High to Low' },
  { id: 'duration_high', label: 'Duration: Longest First' },
  { id: 'title_az', label: 'Title: A-Z' },
];

// Date filter modes
const DATE_FILTER_MODES = [
  { id: 'none', label: 'All Dates' },
  { id: 'specific', label: 'Specific Date' },
  { id: 'month', label: 'Specific Month' },
  { id: 'range', label: 'Date Range' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function SeminarsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [seminars, setSeminars] = useState([]);
  const [filteredSeminars, setFilteredSeminars] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedIndustries, setSelectedIndustries] = useState(() => {
    const industry = searchParams.get('industry');
    return industry ? [industry] : [];
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const type = searchParams.get('type');
    return type ? [type] : [];
  });
  const [selectedSpeaker, setSelectedSpeaker] = useState('all');
  
  // Sort
  const [sortBy, setSortBy] = useState('newest');
  
  // Date filters
  const [dateFilterMode, setDateFilterMode] = useState('none');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  // Handle URL query params on mount
  useEffect(() => {
    const type = searchParams.get('type');
    if (type) {
      setSelectedTypes([type]);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchIndustries();
    fetchSpeakers();
    fetchSeminars();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [seminars, selectedIndustries, selectedTypes, selectedSpeaker, search, currentPage, sortBy, dateFilterMode, selectedDate, selectedMonth, selectedYear, dateRange]);

  const fetchIndustries = async () => {
    try {
      // Fetch industries with tree structure (parent/child with └ prefix)
      const response = await industriesAPI.getFlat();
      setIndustries(response.data || []);
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const fetchSpeakers = async () => {
    try {
      const response = await speakersAPI.getAll({ limit: 500 });
      setSpeakers(response.data || []);
    } catch (error) {
      console.error('Error fetching speakers:', error);
    }
  };

  const fetchSeminars = async () => {
    setLoading(true);
    try {
      const response = await seminarsAPI.getAll({});
      setSeminars(response.data || []);
    } catch (error) {
      console.error('Error fetching seminars:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...seminars];

    // Search filter — matches title, description, code, tags, topics, speakers
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      filtered = filtered.filter(s => {
        const speakerNames = Array.isArray(s.speaker_names)
          ? s.speaker_names.join(' ').toLowerCase()
          : (Array.isArray(s.speakers)
              ? s.speakers.map(sp => `${sp.first_name || ''} ${sp.last_name || ''}`).join(' ').toLowerCase()
              : '');
        const tags = Array.isArray(s.tags) ? s.tags.join(' ').toLowerCase() : '';
        const areas = Array.isArray(s.areas_covered) ? s.areas_covered.join(' ').toLowerCase() : '';
        return (
          (s.title || '').toLowerCase().includes(q) ||
          (s.description || '').toLowerCase().includes(q) ||
          (s.short_description || '').toLowerCase().includes(q) ||
          (s.overview || '').toLowerCase().includes(q) ||
          (s.webinar_code || '').toLowerCase().includes(q) ||
          (s.legacy_webinar_code || '').toLowerCase().includes(q) ||
          (s.who_should_attend || '').toLowerCase().includes(q) ||
          (s.target_audience || '').toLowerCase().includes(q) ||
          (s.location || '').toLowerCase().includes(q) ||
          tags.includes(q) ||
          areas.includes(q) ||
          speakerNames.includes(q)
        );
      });
    }

    // Industry filter
    if (selectedIndustries.length > 0) {
      filtered = filtered.filter(s => selectedIndustries.includes(s.industry_id));
    }

    // Type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(s => {
        if (selectedTypes.includes('live') && s.seminar_type === 'live') return true;
        if (selectedTypes.includes('recorded') && s.seminar_type === 'on_demand') return true;
        if (selectedTypes.includes('certified') && s.has_cpe_credits) return true;
        if (selectedTypes.includes('series') && s.is_part_of_series) return true;
        if (selectedTypes.includes('bestseller') && s.is_bestseller) return true;
        return false;
      });
    }

    // Date filter
    if (dateFilterMode !== 'none') {
      filtered = filtered.filter(s => {
        if (!s.scheduled_date) return false;
        const seminarDate = parseISO(s.scheduled_date);
        
        if (dateFilterMode === 'specific' && selectedDate) {
          return isSameDay(seminarDate, selectedDate);
        }
        
        if (dateFilterMode === 'month') {
          const monthStart = startOfMonth(new Date(selectedYear, selectedMonth));
          const monthEnd = endOfMonth(new Date(selectedYear, selectedMonth));
          return isWithinInterval(seminarDate, { start: monthStart, end: monthEnd });
        }
        
        if (dateFilterMode === 'range' && dateRange.from && dateRange.to) {
          return isWithinInterval(seminarDate, { start: dateRange.from, end: dateRange.to });
        }
        
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.scheduled_date || 0) - new Date(a.scheduled_date || 0);
        case 'oldest':
          return new Date(a.scheduled_date || 0) - new Date(b.scheduled_date || 0);
        case 'price_low':
          return (a.price || 0) - (b.price || 0);
        case 'price_high':
          return (b.price || 0) - (a.price || 0);
        case 'duration_high':
          return (b.duration_hours || 0) - (a.duration_hours || 0);
        case 'title_az':
          return (a.title || '').localeCompare(b.title || '');
        default:
          return 0;
      }
    });

    // Calculate pagination
    const total = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    setTotalPages(total || 1);

    if (currentPage > total && total > 0) {
      setCurrentPage(1);
    }

    // Apply pagination
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedResults = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    setFilteredSeminars(paginatedResults);

    // Update URL params
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedIndustries.length === 1) params.set('industry', selectedIndustries[0]);
    if (selectedTypes.length === 1) params.set('type', selectedTypes[0]);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params);
  }, [seminars, selectedIndustries, selectedTypes, search, currentPage, sortBy, dateFilterMode, selectedDate, selectedMonth, selectedYear, dateRange, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const toggleIndustry = (industryId) => {
    setSelectedIndustries(prev => 
      prev.includes(industryId) 
        ? prev.filter(id => id !== industryId)
        : [...prev, industryId]
    );
    setCurrentPage(1);
  };

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedIndustries([]);
    setSelectedTypes([]);
    setSelectedSpeaker('all');
    setDateFilterMode('none');
    setSelectedDate(null);
    setDateRange({ from: null, to: null });
    setCurrentPage(1);
    setSearchParams({});
  };

  const clearDateFilter = () => {
    setDateFilterMode('none');
    setSelectedDate(null);
    setDateRange({ from: null, to: null });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return formatET(dateStr, 'MMM dd, yyyy');
  };

  const formatDayDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return `${format(date, 'EEE')}, ${format(date, 'MMM dd, yyyy')}`;
  };

  const getDateFilterLabel = () => {
    if (dateFilterMode === 'specific' && selectedDate) {
      return format(selectedDate, 'MMM dd, yyyy');
    }
    if (dateFilterMode === 'month') {
      return `${MONTHS[selectedMonth]} ${selectedYear}`;
    }
    if (dateFilterMode === 'range' && dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd, yyyy')}`;
    }
    return null;
  };

  const hasActiveFilters = search || selectedIndustries.length > 0 || selectedTypes.length > 0 || dateFilterMode !== 'none';
  const activeFilterCount = (search ? 1 : 0) + selectedIndustries.length + selectedTypes.length + (dateFilterMode !== 'none' ? 1 : 0);
  
  const totalFilteredCount = seminars.filter(s => {
    let match = true;
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      match = match && (
        s.title?.toLowerCase().includes(searchLower) ||
        s.description?.toLowerCase().includes(searchLower)
      );
    }
    if (selectedIndustries.length > 0) {
      match = match && selectedIndustries.includes(s.industry_id);
    }
    if (selectedTypes.length > 0) {
      match = match && (
        (selectedTypes.includes('live') && s.seminar_type === 'live') ||
        (selectedTypes.includes('recorded') && s.seminar_type === 'on_demand') ||
        (selectedTypes.includes('certified') && s.has_cpe_credits) ||
        (selectedTypes.includes('series') && s.is_part_of_series) ||
        (selectedTypes.includes('bestseller') && s.is_bestseller)
      );
    }
    return match;
  }).length;

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-12" data-testid="pagination">
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="border-slate-300"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </Button>

        <div className="flex items-center gap-1">
          {startPage > 1 && (
            <>
              <Button
                variant={currentPage === 1 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => goToPage(1)}
                className={currentPage === 1 ? 'bg-navy-600' : ''}
              >
                1
              </Button>
              {startPage > 2 && <span className="px-2 text-slate-400">...</span>}
            </>
          )}

          {pages.map(page => (
            <Button
              key={page}
              variant={currentPage === page ? 'default' : 'ghost'}
              size="sm"
              onClick={() => goToPage(page)}
              className={currentPage === page ? 'bg-navy-600' : ''}
            >
              {page}
            </Button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-2 text-slate-400">...</span>}
              <Button
                variant={currentPage === totalPages ? 'default' : 'ghost'}
                size="sm"
                onClick={() => goToPage(totalPages)}
                className={currentPage === totalPages ? 'bg-navy-600' : ''}
              >
                {totalPages}
              </Button>
            </>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="border-slate-300"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear + i);

  return (
    <div className="min-h-screen bg-transparent" data-testid="seminars-page">
      {/* Hero Section - MailCraft light editorial */}
      <section className="bg-white border-b border-zinc-200 pt-28 pb-16">
        <div className="container-custom">
          <Breadcrumb />
          <div className="max-w-3xl">
            <span className="mono-label inline-flex items-center gap-2 text-xs text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
              Seminar Catalog
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
              <span className="text-zinc-900">Explore our </span>
              <span className="text-zinc-400">seminars.</span>
            </h1>
            <p className="text-zinc-600 text-lg leading-relaxed">
              In-depth training sessions from 3 to 8 hours. Single or multi-day seminars 
              designed for comprehensive learning on governance, risk, and compliance topics.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <div className="flex items-center gap-2 bg-zinc-100 border border-zinc-200 rounded-lg px-4 py-2">
                <Clock className="w-4 h-4 text-navy-600" />
                <span className="text-zinc-700 text-sm">3-8 Hours Per Session</span>
              </div>
              <div className="flex items-center gap-2 bg-zinc-100 border border-zinc-200 rounded-lg px-4 py-2">
                <Building2 className="w-4 h-4 text-navy-600" />
                <span className="text-zinc-700 text-sm">Corporate Training Available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Bar - Single Line Below Hero */}
      <section className="py-6 bg-white border-b border-zinc-200 sticky top-[72px] z-30">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap items-center gap-3">
              {/* Seminar Count */}
              <span className="text-sm font-medium text-navy-700 whitespace-nowrap">
                {totalFilteredCount} Seminars
              </span>
              
              <div className="h-6 w-px bg-slate-300 hidden sm:block" />
              
              {/* All Seminars Filter */}
              <Select 
                value={selectedTypes.length === 1 ? selectedTypes[0] : 'all'} 
                onValueChange={(v) => {
                  if (v === 'all') {
                    setSelectedTypes([]);
                  } else {
                    setSelectedTypes([v]);
                  }
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-36 h-9 text-sm border-slate-300" data-testid="type-filter">
                  <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Seminars" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Seminars</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="recorded">Recorded</SelectItem>
                  <SelectItem value="certified">Certified</SelectItem>
                  <SelectItem value="series">Series</SelectItem>
                  <SelectItem value="bestseller">Best Seller</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Industries Filter */}
              <SearchableSelect
                options={[
                  { value: 'all', label: 'All Industries' },
                  ...industries.map((ind) => ({ value: ind.id, label: ind.name }))
                ]}
                value={selectedIndustries.length === 1 ? selectedIndustries[0] : 'all'}
                onValueChange={(v) => {
                  if (v === 'all' || !v) {
                    setSelectedIndustries([]);
                  } else {
                    setSelectedIndustries([v]);
                  }
                  setCurrentPage(1);
                }}
                placeholder="All Industries"
                searchPlaceholder="Search industries..."
                className="w-48 border-slate-300"
                icon={<Filter className="w-3.5 h-3.5 shrink-0 text-slate-400" />}
                dataTestId="industry-filter"
                preserveOrder={true}
              />
              
              {/* Speakers Filter */}
              <SearchableSelect
                options={[
                  { value: 'all', label: 'All Speakers' },
                  ...speakers.map((sp) => ({ value: sp.id, label: `${sp.first_name} ${sp.last_name}` }))
                ]}
                value={selectedSpeaker}
                onValueChange={(v) => {
                  setSelectedSpeaker(v || 'all');
                  setCurrentPage(1);
                }}
                placeholder="All Speakers"
                searchPlaceholder="Search speakers..."
                className="w-48 border-slate-300"
                icon={<Filter className="w-3.5 h-3.5 shrink-0 text-slate-400" />}
                dataTestId="speaker-filter"
              />
              
              {/* Date Filter with Calendar Popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-40 h-9 text-sm border-slate-300 justify-start font-normal"
                    data-testid="date-filter"
                  >
                    <CalendarDays className="w-3.5 h-3.5 mr-2 text-slate-400" />
                    {dateFilterMode === 'none' && 'All Dates'}
                    {dateFilterMode === 'specific' && selectedDate && format(selectedDate, 'MMM dd, yyyy')}
                    {dateFilterMode === 'specific' && !selectedDate && 'Select Date'}
                    {dateFilterMode === 'month' && `${MONTHS[selectedMonth]} ${selectedYear}`}
                    {dateFilterMode === 'range' && dateRange.from && dateRange.to && 
                      `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd')}`}
                    {dateFilterMode === 'range' && (!dateRange.from || !dateRange.to) && 'Select Range'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  {/* Date Filter Mode Tabs */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      onClick={() => { setDateFilterMode('none'); setSelectedDate(null); setDateRange({ from: null, to: null }); }}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                        dateFilterMode === 'none' ? 'border-navy-500 bg-navy-50 text-navy-700' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      All Dates
                    </button>
                    <button
                      onClick={() => setDateFilterMode('specific')}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                        dateFilterMode === 'specific' ? 'border-navy-500 bg-navy-50 text-navy-700' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      Specific Date
                    </button>
                    <button
                      onClick={() => setDateFilterMode('month')}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                        dateFilterMode === 'month' ? 'border-navy-500 bg-navy-50 text-navy-700' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      By Month
                    </button>
                    <button
                      onClick={() => setDateFilterMode('range')}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                        dateFilterMode === 'range' ? 'border-navy-500 bg-navy-50 text-navy-700' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      Date Range
                    </button>
                  </div>

                  {/* Specific Date Calendar */}
                  {dateFilterMode === 'specific' && (
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      className="rounded-md border border-slate-200"
                    />
                  )}

                  {/* Month/Year Selector */}
                  {dateFilterMode === 'month' && (
                    <div className="space-y-3">
                      <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((month, idx) => (
                            <SelectItem key={idx} value={idx.toString()}>{month}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {yearOptions.map((year) => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Date Range Calendar */}
                  {dateFilterMode === 'range' && (
                    <div>
                      <CalendarComponent
                        mode="range"
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={1}
                        className="rounded-md border border-slate-200"
                      />
                      {dateRange.from && dateRange.to && (
                        <p className="text-xs text-slate-500 mt-2 text-center">
                          {format(dateRange.from, 'MMM dd, yyyy')} - {format(dateRange.to, 'MMM dd, yyyy')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Clear Button */}
                  {dateFilterMode !== 'none' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setDateFilterMode('none'); setSelectedDate(null); setDateRange({ from: null, to: null }); }}
                      className="w-full mt-3 text-slate-600"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear Date Filter
                    </Button>
                  )}
                </PopoverContent>
              </Popover>
              
              {/* Spacer */}
              <div className="flex-1" />
              
              {/* Search Bar */}
              <form onSubmit={handleSearch} className="w-64">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search by title, code, speaker, topic..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-9 pr-4 h-9 text-sm border-slate-300"
                    data-testid="search-input"
                  />
                </div>
              </form>
            </div>
            
            {/* Active Filters Pills */}
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-500">Active:</span>
                {search && (
                  <Badge 
                    className="bg-slate-100 text-slate-700 hover:bg-slate-200 cursor-pointer text-xs px-2 py-0.5"
                    onClick={() => setSearch('')}
                  >
                    "{search}" <X className="w-3 h-3 ml-1" />
                  </Badge>
                )}
                {selectedTypes.map(typeId => {
                  const type = SEMINAR_TYPES.find(t => t.id === typeId);
                  return (
                    <Badge 
                      key={typeId}
                      className="bg-navy-100 text-navy-700 hover:bg-navy-200 cursor-pointer text-xs px-2 py-0.5"
                      onClick={() => toggleType(typeId)}
                    >
                      {type?.label} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  );
                })}
                {selectedIndustries.map(industryId => {
                  const industry = industries.find(i => i.id === industryId);
                  return (
                    <Badge 
                      key={industryId}
                      className="bg-gold-100 text-gold-700 hover:bg-gold-200 cursor-pointer text-xs px-2 py-0.5"
                      onClick={() => toggleIndustry(industryId)}
                    >
                      {industry?.name} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  );
                })}
                {dateFilterMode !== 'none' && getDateFilterLabel() && (
                  <Badge 
                    className="bg-zinc-100 text-zinc-700 hover:bg-zinc-200 cursor-pointer text-xs px-2 py-0.5"
                    onClick={clearDateFilter}
                  >
                    {getDateFilterLabel()} <X className="w-3 h-3 ml-1" />
                  </Badge>
                )}
                <button
                  onClick={clearFilters}
                  className="text-xs text-slate-500 hover:text-navy-700 ml-2"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content - 3 Column Grid */}
      <section className="py-8">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            {/* Seminar Results */}
            {loading && filteredSeminars.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="animate-pulse bg-white border border-zinc-200 rounded-xl p-5">
                    <div className="flex justify-between mb-4">
                      <div className="h-5 w-24 bg-zinc-200 rounded-full" />
                      <div className="h-5 w-16 bg-zinc-200 rounded-full" />
                    </div>
                    <div className="h-4 bg-zinc-200 rounded w-1/3 mb-4" />
                    <div className="h-5 bg-zinc-200 rounded w-3/4 mb-2" />
                    <div className="h-5 bg-zinc-200 rounded w-2/3 mb-5" />
                    <div className="h-4 bg-zinc-200 rounded w-full mb-2" />
                    <div className="h-4 bg-zinc-200 rounded w-1/2 mb-5" />
                    <div className="flex justify-between pt-4 border-t border-zinc-100">
                      <div className="h-6 w-12 bg-zinc-200 rounded" />
                      <div className="h-8 w-24 bg-zinc-200 rounded-md" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredSeminars.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 mx-auto bg-zinc-100 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-zinc-900 mb-2">
                  No seminars found
                </h3>
                <p className="text-zinc-600 mb-4 max-w-md mx-auto text-sm">
                  Try adjusting your filters or search terms.
                </p>
                <Button onClick={clearFilters} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white">
                  Clear Filters
                </Button>
              </div>
            ) : (
              /* Grid View - image-free MailCraft cards */
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                {filteredSeminars.map((seminar, idx) => (
                  <WebinarCard key={seminar.id} item={seminar} type="seminar" animationDelay={idx * 0.03} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredSeminars.length > 0 && renderPagination()}
          </div>
        </div>
      </section>
    </div>
  );
}
